import { useAtom } from '@reatom/npm-react'
import React from 'react'
import { styled } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { reatomPremiumBanner } from '@root/entities/currency/atoms/reatom-currency-banner'
import { useCurrencyIcon } from '@root/entities/currency/ui/currency-icon/use-currency-icon'
import { reatomImage, useOptionalAtom } from '@root/shared/lib'
import { RectangleNetworkBackground } from '@root/shared/ui'
import { TooltipCard } from '@root/shared/ui-kit/tooltip-card/tooltip-card'
import { useTooltipCardParentGuardContext } from '@root/shared/ui-kit/tooltip-card/tooltip-card-parent-guard-context'

import { CurrencyIconPopoverDuplicates } from './currency-icon-popover-duplicates'
import { CurrencyIconPopoverMore } from './currency-icon-popover-more'

import type { CurrencyModel, ImageHash } from '@root/entities/currency/model'
import type { PremiumMetaModel } from '@root/entities/premium'
import type { TooltipCardProps } from '@root/shared/ui-kit'

export type CurrencyIconPopoverProps = {
  children: React.ReactElement
  currency: CurrencyModel
  premiumInfo: PremiumMetaModel | undefined
  iconSrcOverride?: string
  bannerSrcOverride?: string | 'none' | undefined
  imageHashOverride?: ImageHash | 'none' | undefined
  reusedImages?: {
    side?: TooltipCardProps['side']
  }
}

const CurrencyIconPopover_: React.FC<CurrencyIconPopoverProps> = React.memo(
  ({
    children,
    currency,
    premiumInfo,
    iconSrcOverride,
    imageHashOverride,
    bannerSrcOverride,
    reusedImages,
  }: CurrencyIconPopoverProps) => {
    const withReusedImagesBody = reusedImages != undefined
    const reusedImagesSide = reusedImages?.side ?? 'bottom'
    const parentGuard = useTooltipCardParentGuardContext()
    const { state: iconState } = useCurrencyIcon({
      currency,
      iconSrcOverride,
    })

    const [bannerModel] = useAtom(
      (ctxBanner) => {
        if (bannerSrcOverride === 'none') {
          return undefined
        }
        if (bannerSrcOverride) {
          return reatomImage({ src: bannerSrcOverride })
        }
        if (!premiumInfo) {
          return undefined
        }
        const status = ctxBanner.spy(premiumInfo.status)
        if (status !== 'active') {
          return undefined
        }

        return reatomPremiumBanner({
          premiumModel: premiumInfo,
        })
      },
      [premiumInfo, bannerSrcOverride],
    )

    const bannerState = useOptionalAtom(bannerModel)
    const hasReusedImages = (currency.reusedImageCount ?? 0) > 0

    const renderCloseArea = () => {
      if (!AsyncStateN.isSuccess(iconState) || !iconState.value) {
        return undefined
      }

      if (
        bannerState &&
        AsyncStateN.isSuccess(bannerState) &&
        bannerState.value
      ) {
        return (
          <PreviewWithBanner>
            <Preview
              data-with-banner="true"
              src={iconState.value}
              alt={currency.ticker}
            />
            <BannerPreview src={bannerState.value} alt={currency.ticker} />
          </PreviewWithBanner>
        )
      }

      return <Preview src={iconState.value} alt={currency.ticker} />
    }

    const closeAreaContent = renderCloseArea()
    if (!closeAreaContent) {
      return children
    }

    return (
      <Root
        closeOnCloseAreaMouseEnter={!withReusedImagesBody || !hasReusedImages}
        ignoreParentCloseAreaSuppression={
          parentGuard.ignoreParentCloseAreaSuppression === true
        }
        closeOnExternalScroll={!hasReusedImages}
        disableBuiltInWheelForwarding={!hasReusedImages}
        closeArea={
          <CurrencyIconPopoverMore
            reusedImageCount={currency.reusedImageCount}
            withReusedImagesBody={withReusedImagesBody}
          >
            {closeAreaContent}
          </CurrencyIconPopoverMore>
        }
        clickArea={
          hasReusedImages && withReusedImagesBody ? (
            <CurrencyIconPopoverDuplicates
              imageHash={
                imageHashOverride === 'none'
                  ? undefined
                  : (imageHashOverride ?? currency.imageHash)
              }
              network={currency.network}
              address={currency.address}
              side={reusedImagesSide}
            />
          ) : undefined
        }
      >
        {children as React.ReactElement<React.RefAttributes<HTMLElement>>}
      </Root>
    )
  },
)

export const CurrencyIconPopover: React.FC<CurrencyIconPopoverProps> = ({
  currency,
  children,
  ...props
}) => {
  const [iconStatus] = useAtom(currency.iconStatus)

  const shouldWrap = props.iconSrcOverride || iconStatus === 'active'

  if (!shouldWrap) {
    return children
  }

  return (
    <CurrencyIconPopover_ currency={currency} {...props}>
      {children}
    </CurrencyIconPopover_>
  )
}

const Preview = styled.img`
  ${RectangleNetworkBackground}

  height: 220px;
  width: 220px;
  border-radius: 6px;
  display: block;
  object-fit: cover;

  &[data-with-banner='true'] {
    height: 150px;
    width: 150px;
  }
`

const BannerPreview = styled.img`
  height: 150px;
  aspect-ratio: 3/1;
  border-radius: 6px;
  display: block;
  object-fit: cover;

  ${RectangleNetworkBackground}
  background-color: var(--background-1);
`

const PreviewWithBanner = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  gap: 10px;
`

const Root = styled(TooltipCard)`
  --tooltip-width: auto;
  --root-padding: 6px;
`
